import { API_BASE_URL } from './apiBase';

// Utility functions for Cloudinary management

export interface CloudinaryDeleteResult {
  success: boolean;
  deleted: string[];
  errors: { publicId: string; error: string }[];
}

/**
 * Generate optimized image URL for different contexts
 */
export const getOptimizedImageUrl = (
  originalUrl: string, 
  context: 'card' | 'modal' | 'admin' | 'hero' = 'card'
): string => {
  if (!originalUrl) return '';
  
  // If it's not a Cloudinary URL, return as-is
  if (!originalUrl.includes('cloudinary.com')) {
    return originalUrl;
  }
  
  const transformations = {
    card: 'w_400,h_300,c_fill,q_auto,f_auto', // Package cards
    modal: 'w_800,h_600,c_fill,q_auto,f_auto', // Package modals
    admin: 'w_200,h_150,c_fill,q_auto,f_auto', // Admin thumbnails
    hero: 'w_1200,h_630,c_fill,q_auto,f_auto' // Hero images
  };
  
  return originalUrl.replace('/upload/', `/upload/${transformations[context]}/`);
};

/**
 * Delete images from Cloudinary 
 * **NOTE: This will permanently delete images from your Cloudinary account**
 */
export const deleteCloudinaryImages = async (publicIds: string[]): Promise<CloudinaryDeleteResult> => {
  if (!publicIds || publicIds.length === 0) {
    return { success: true, deleted: [], errors: [] };
  }

  try {
    const response = await fetch(`${API_BASE_URL}/cloudinary/delete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ publicIds }),
    });
    if (!response.ok) {
      throw new Error('Image deletion was rejected by the server');
    }
    return await response.json();
  } catch (error) {
    console.error('Cloudinary deletion failed:', error);
    const errors = publicIds.map(id => ({ publicId: id, error: (error as Error).message }));
    
    return {
      success: false,
      deleted: [],
      errors
    };
  }
};

/**
 * Get all image URLs/publicIds from a gallery for deletion reference
 */
export const extractGalleryImageIds = (gallery: any): string[] => {
  const publicIds: string[] = [];
  
  if (gallery.images && Array.isArray(gallery.images)) {
    gallery.images.forEach((img: any) => {
      if (img.publicId && img.publicId !== 'placeholder') {
        publicIds.push(img.publicId);
      }
    });
  }
  
  return publicIds.filter(id => id && id !== 'placeholder');
};



